/**
 * Address input (input_subtype: address) — Alpine component.
 * Moved from the inline script in components/address-input.blade.php: inline
 * blade scripts die when the component first appears via a Livewire morph
 * (morphdom doesn't execute injected script tags), which left the whole field
 * dead — no autocomplete AND no way to save manual input.
 *
 * Manual entry binds unconditionally in init(); Google Places is a progressive
 * enhancement layered on top when the central loader resolves. Every keystroke
 * persists the raw string (legacy plain-string format) so a typed address
 * survives Continue even if the user never presses Enter or selects a
 * suggestion — with or without Google.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';
import { unitField } from './unit-field';

export default function enhancedAddressAutocomplete(modelValue) {
    return {
        autocomplete: null,
        showParsedFields: false,
        modelValue: modelValue,
        parsedAddress: {
            formatted: '',
            street: '',
            street2: '',
            address2: '',
            city: '',
            state: '',
            country: '',
            zip: ''
        },

        // Apt/Ste/Flr asked as a choice plus a number, stored back into
        // address2 as one canonical string — the payload shape is unchanged.
        ...unitField('parsedAddress.address2'),

        init() {
            this.bindManualEntry();

            loadGoogleMaps()
                .then(() => this.initAutocomplete())
                .catch(() => {}); // silent fallback — manual entry already works

            // Parse existing value if present
            this.parseExistingValue();
            this.watchUnit();

            this.$watch('modelValue', (value) => {
                if (!this.showParsedFields) {
                    this.parseExistingValue();
                }
            });
        },

        /** Always-on manual path: never depends on Google. */
        bindManualEntry() {
            const input = this.$refs.addressInput;
            if (!input) return;

            // Persist raw text on every keystroke so nothing is lost if the
            // user taps Continue without Enter/blur/selection.
            input.addEventListener('input', () => {
                if (!this.showParsedFields) {
                    this.persistRawText(input.value);
                }
            });

            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.commitManual(input.value);
                }
            });

            // Delay lets a click on a Places suggestion win: place_changed sets
            // showParsedFields before this timeout inspects it.
            input.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.showParsedFields && input.value.trim()) {
                        this.commitManual(input.value);
                    }
                }, 250);
            });
        },

        persistRawText(value) {
            const v = value.trim();
            this.modelValue = v;
            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = v;
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        },

        commitManual(value) {
            const v = (value || '').trim();
            if (!v) return;
            this.parsedAddress.formatted = v;
            this.parsedAddress.street = v;
            this.showParsedFields = true;
            this.updateValue();
        },

        parseExistingValue() {
            // Don't overwrite a field the user is actively editing — the
            // modelValue watcher fires on our own keystrokes too, which clobbered
            // fast typing (chars dropped / value flashed).
            if (this.$el && this.$el.contains(document.activeElement)) return;
            const value = this.modelValue || '';
            if (!value) return;

            // Try to parse as JSON first
            try {
                const json = JSON.parse(value);
                if (json.formatted !== undefined) {
                    this.parsedAddress = {
                        formatted: json.formatted || '',
                        street: json.street || '',
                        street2: json.street2 || '',
                        address2: json.address2 || '',
                        city: json.city || '',
                        state: json.state || '',
                        country: json.country || '',
                        zip: json.zip || ''
                    };
                    this.showParsedFields = true;
                    return;
                }
            } catch (e) {
                // Not JSON
            }

            // Treat as plain string (legacy format)
            if (this.$refs.addressInput) {
                this.$refs.addressInput.value = value;
            }
        },

        initAutocomplete() {
            const input = this.$refs.addressInput;
            if (!input || this.autocomplete) return;

            this.autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ["address_components", "formatted_address"],
                types: ["address"]
            });

            this.autocomplete.addListener("place_changed", () => {
                const place = this.autocomplete.getPlace();

                if (place.address_components) {
                    this.parseAddressComponents(place);
                    this.showParsedFields = true;
                    this.updateValue();

                    // Close dropdown
                    input.blur();
                }
            });
        },

        parseAddressComponents(place) {
            // Shared parser: country-aware city/street fallbacks (UK
            // postal_town, sublocality wards, …).
            const p = alvvaParsePlace(place);
            this.parsedAddress = {
                formatted: p.formatted,
                street: p.street,
                street2: '',
                address2: '',
                city: p.city,
                state: p.state,
                country: p.country,
                zip: p.zip
            };
        },

        resetToSearch() {
            this.showParsedFields = false;
            this.parsedAddress = {
                formatted: '',
                street: '',
                street2: '',
                address2: '',
                city: '',
                state: '',
                country: '',
                zip: ''
            };

            // Clear the search input
            this.$nextTick(() => {
                if (this.$refs.addressInput) {
                    this.$refs.addressInput.value = '';
                    this.$refs.addressInput.focus();
                }
            });

            // Clear the model value
            this.modelValue = '';
            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = '';
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        },

        buildFormattedAddress() {
            const parts = [];

            if (this.parsedAddress.street) {
                parts.push(this.parsedAddress.street);
            }
            if (this.parsedAddress.street2) {
                parts.push(this.parsedAddress.street2);
            }
            if (this.parsedAddress.address2) {
                parts.push(this.parsedAddress.address2);
            }
            if (this.parsedAddress.city) {
                parts.push(this.parsedAddress.city);
            }
            if (this.parsedAddress.state) {
                parts.push(this.parsedAddress.state);
            }
            if (this.parsedAddress.zip) {
                parts.push(this.parsedAddress.zip);
            }
            if (this.parsedAddress.country) {
                parts.push(this.parsedAddress.country);
            }

            return parts.join(', ');
        },

        updateValue() {
            // Update formatted address
            this.parsedAddress.formatted = this.buildFormattedAddress();

            // Store as JSON
            const jsonValue = JSON.stringify(this.parsedAddress);

            this.modelValue = jsonValue;

            // Update hidden input and trigger Livewire
            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = jsonValue;
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }
}
